import { Component, Input } from '@angular/core';
import { BranchData } from '../../models/sell.models';
import { MarketPulseSoldExample } from '../../../../models/market-pulse.model';

@Component({
  selector: 'pla-plate-x-flow',
  templateUrl: './plate-x-flow.component.html',
  styleUrls: ['./plate-x-flow.component.scss'],
})
export class PlateXFlowComponent {
  // Stage 01 — illustrative valuation preview. PLA 73X is the curated plate that
  // follows through Stages 01 and 02 in the /sell flow (build PR #21 design intent).
  @Input() exampleValuationPlate: string = 'PLA 73X';
  @Input() exampleRange: string = '\u00A31,000 \u2013 \u00A31,400';
  @Input() exampleComparablesNote: string = '3 comparables';
  @Input() exampleFormatNote: string = 'Suffix \u00B7 readable';
  @Input() exampleLastComp: string = 'Last comp Mar 2025';

  // Stage 02 — same plate (PLA 73X) viewed from the dealer-routing side.
  @Input() exampleRegistration: string = 'PLA 73X';
  @Input() exampleDealerRange: string = '\u00A31,000\u2013\u00A31,400';
  @Input() exampleDealerReserve: string = '\u00A31,200';
  @Input() exampleDealerFilter: string = 'Matches dealer filter: suffix format';

  // Stage 04 — populated from /api/marketpulse when available, else falls back to the image.
  @Input() soldExample: MarketPulseSoldExample | null = null;
  @Input() soldVisualSrc: string = '/assets/sell/sold_visual.png';
  @Input() soldVisualAlt: string = 'Sold-data visual showing example plate sale lifecycle';

  cashBranch: BranchData = {
    feedItems: [
      { name: 'Dealer A', amount: '\u00A34,100', note: 'outright purchase' },
      { name: 'Dealer B', amount: '\u00A33,800', note: 'outright purchase' },
    ],
    dataCells: [
      { label: 'Best for', value: 'Dateless, initials, names, rare formats' },
      { label: 'Typical speed', value: '7\u201314 days' },
      { label: 'Price expectation', value: 'Wholesale tier' },
      { label: 'Outcome', value: 'Sale agreement with the dealer' },
    ],
    footNote: 'If no bid suits you, the listing route is there.',
  };

  listingBranch: BranchData = {
    feedItems: [
      { name: 'Dealer C', amount: '\u00A35,400 min', note: 'marks up to cover fee' },
      { name: 'Dealer D', amount: '\u00A35,200 min', note: '8% fee \u00B7 asking \u00A36,500' },
      { name: 'Dealer E', amount: '\u00A34,950 min', note: '\u00A3450 flat fee \u00B7 asking \u00A36,200' },
    ],
    dataCells: [
      { label: 'Best for', value: 'Most plates \u00B7 broader market' },
      { label: 'Typical speed', value: '60\u2013120 days' },
      { label: 'Price expectation', value: 'Retail-linked' },
      { label: 'Outcome', value: 'Listing agreement with the dealer' },
    ],
    footNote: 'PlateX activates the listing here too. The chosen dealer markets it on their site; we surface it on PlateX.',
  };

  get soldExamplePlate(): string {
    if (!this.soldExample) return '';
    return this.soldExample.formattedRegistration || this.soldExample.registration || '';
  }

  get soldExamplePrice(): string {
    if (!this.soldExample) return '';
    const pounds = Math.round((this.soldExample.soldPrice || 0) / 100);
    return `\u00A3${pounds.toLocaleString('en-GB')}`;
  }

  get soldExampleDate(): string {
    if (!this.soldExample) return '';
    const d = new Date(this.soldExample.soldDate);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  }
}
